/* ==========================================================================
 * redesk-datalayer.js — Helper canonique pour les événements form_submit.
 *
 * Expose:
 *   window.RedeskDL.pushFormSubmit({ form_id, intent, values, consent })
 *
 * Schéma poussé:
 *   {
 *     event: "redesk_form_submit",
 *     form_id, intent, page, timestamp, consent,
 *     user_data: { email, email_sha256, phone, phone_sha256, firstname, ... }
 *   }
 *
 * `user_data` est inclus uniquement si consent === true ET DNT !== '1'.
 * Schéma documenté: docs/agents/datalayer-schema.md
 * ========================================================================== */

(function () {
  'use strict';

  if (window.RedeskDL && window.RedeskDL.pushFormSubmit) return;

  function isDNT() {
    return typeof navigator !== 'undefined' && navigator.doNotTrack === '1';
  }

  function normalizeEmail(s) {
    if (typeof s !== 'string') return '';
    return s.trim().toLowerCase();
  }

  // Normalize FR phone to E.164. Returns "" if not parseable.
  // Matches the pattern enforced by the form validators: /^(\+33|0)[1-9]\d{8}$/.
  function normalizePhoneE164(s) {
    if (typeof s !== 'string') return '';
    var stripped = s.replace(/[\s.\-()]/g, '');
    if (/^\+33[1-9]\d{8}$/.test(stripped)) return stripped;
    if (/^0[1-9]\d{8}$/.test(stripped))    return '+33' + stripped.slice(1);
    return '';
  }

  function toHex(buf) {
    var bytes = new Uint8Array(buf);
    var out = '';
    for (var i = 0; i < bytes.length; i++) {
      var h = bytes[i].toString(16);
      out += (h.length === 1 ? '0' : '') + h;
    }
    return out;
  }

  function sha256Hex(s) {
    if (!s) return Promise.resolve('');
    if (!window.crypto || !window.crypto.subtle || typeof TextEncoder === 'undefined') {
      return Promise.resolve('');
    }
    try {
      var data = new TextEncoder().encode(s);
      return window.crypto.subtle.digest('SHA-256', data).then(toHex, function () { return ''; });
    } catch (_) {
      return Promise.resolve('');
    }
  }

  function pickFirstString(obj, keys) {
    for (var i = 0; i < keys.length; i++) {
      var v = obj[keys[i]];
      if (typeof v === 'string' && v.length) return v;
    }
    return '';
  }

  function buildBaseEvent(form_id, intent, consent) {
    return {
      event: 'redesk_form_submit',
      form_id: form_id || null,
      intent: intent || null,
      page: (typeof location !== 'undefined') ? location.pathname : null,
      timestamp: new Date().toISOString(),
      consent: !!consent
    };
  }

  function pushToDataLayer(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  // Public API.
  // Always returns a Promise that resolves once the push happened (or skipped).
  function pushFormSubmit(opts) {
    opts = opts || {};
    var form_id = opts.form_id;
    var intent  = opts.intent;
    var values  = opts.values || {};
    var consent = !!opts.consent;

    var base = buildBaseEvent(form_id, intent, consent);

    if (!consent || isDNT()) {
      pushToDataLayer(base);
      return Promise.resolve(base);
    }

    var email = normalizeEmail(pickFirstString(values, ['email']));
    var phone = normalizePhoneE164(pickFirstString(values, ['phone']));

    return Promise.all([sha256Hex(email), sha256Hex(phone)]).then(function (hashes) {
      var user_data = {};
      if (email) {
        user_data.email = email;
        if (hashes[0]) user_data.email_sha256 = hashes[0];
      }
      if (phone) {
        user_data.phone = phone;
        if (hashes[1]) user_data.phone_sha256 = hashes[1];
      }
      var optionalKeys = ['firstname', 'lastname', 'company', 'company_size'];
      for (var i = 0; i < optionalKeys.length; i++) {
        var k = optionalKeys[i];
        var v = values[k];
        if (typeof v === 'string' && v.length) user_data[k] = v;
      }
      base.user_data = user_data;
      pushToDataLayer(base);
      return base;
    }, function () {
      pushToDataLayer(base);
      return base;
    });
  }

  // Attribution des devis : n8n crée les drafts par l'API, sans session
  // Shopify, donc sans parcours client. On garde la 1re et la dernière visite
  // arrivée avec des UTM, et les formulaires de devis l'envoient avec la
  // demande. Aucune donnée personnelle ici : le gclid n'est lu qu'à l'envoi,
  // dans le cookie _gcl_aw que Google ne pose qu'avec le consentement pub.
  var ATTRIBUTION_KEY = 'redesk_attribution';
  // Une visite compte 90 jours, comme un gclid pour l'import des conversions
  // Ads. Rien n'est effacé à l'envoi : deux devis peuvent venir du même clic.
  var ATTRIBUTION_TTL_MS = 90 * 24 * 3600 * 1000;

  function fresh(touch) {
    return touch && Date.now() - Date.parse(touch.date) <= ATTRIBUTION_TTL_MS ? touch : null;
  }

  // Dernière valeur non vide : les URL Ads portent parfois deux utm_campaign,
  // un modèle cassé (« pmax_{campaignname} ») puis l'id de campagne.
  function lastParam(params, key) {
    var values = params.getAll(key).filter(Boolean);
    return values.length ? values[values.length - 1] : '';
  }

  function captureAttribution() {
    try {
      var params = new URLSearchParams(window.location.search);
      var gclid = params.get('gclid');
      if (!lastParam(params, 'utm_source') && !lastParam(params, 'utm_campaign') && !gclid) return;
      var touch = {
        source: lastParam(params, 'utm_source') || (gclid ? 'google' : ''),
        medium: lastParam(params, 'utm_medium') || (gclid ? 'cpc' : ''),
        campaign: lastParam(params, 'utm_campaign'),
        landing: window.location.pathname,
        date: new Date().toISOString().slice(0, 10)
      };
      var saved = JSON.parse(window.localStorage.getItem(ATTRIBUTION_KEY) || '{}');
      window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify({ first: fresh(saved.first) || touch, last: touch }));
    } catch (e) { /* stockage bloqué : la demande partira sans attribution */ }
  }

  // Objet plat de chaînes, recopié tel quel en attributs du draft par n8n.
  function attribution() {
    var saved = {};
    try { saved = JSON.parse(window.localStorage.getItem(ATTRIBUTION_KEY) || '{}'); } catch (e) {}
    var out = {};
    ['first', 'last'].forEach(function (touch) {
      var kept = fresh(saved[touch]);
      ['source', 'medium', 'campaign', 'landing', 'date'].forEach(function (k) {
        out[touch + '_' + k] = (kept && kept[k]) || '';
      });
    });
    // _gcl_aw = GCL.<horodatage>.<gclid>
    var gcl = /(?:^|;\s*)_gcl_aw=([^;]+)/.exec(document.cookie);
    out.gclid = gcl ? decodeURIComponent(gcl[1]).split('.').slice(2).join('.') : '';
    return out;
  }

  captureAttribution();

  window.RedeskDL = {
    pushFormSubmit: pushFormSubmit,
    attribution: attribution,
    _normalizeEmail: normalizeEmail,
    _normalizePhoneE164: normalizePhoneE164,
    _sha256Hex: sha256Hex
  };
})();
